interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  className = '',
}: SkeletonProps) {
  const baseClasses =
    'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer';

  const variantClasses = {
    text: 'rounded h-4 w-full',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
}

export function SkeletonCard() {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" className="w-3/4 h-5" />
          <Skeleton variant="text" className="w-1/2 h-3" />
        </div>
      </div>
      <Skeleton variant="rectangular" height={12} />
      <Skeleton variant="rectangular" height={12} className="w-2/3" />
    </div>
  );
}

export function SkeletonTaskDetail() {
  return (
    <div className="space-y-5">
      {/* Back link */}
      <Skeleton variant="text" className="w-24 h-4" />
      {/* Header */}
      <div className="flex items-center gap-3">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" className="w-2/3 h-7" />
          <Skeleton variant="text" className="w-1/3 h-4" />
        </div>
      </div>
      {/* Two columns */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-6">
        {/* Left */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
            <Skeleton variant="text" className="w-1/3 h-5" />
            <Skeleton variant="rectangular" height={16} />
            <Skeleton variant="rectangular" height={8} />
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-3">
            <Skeleton variant="text" className="w-1/4 h-5" />
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </div>
        </div>
        {/* Right */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-3">
            <Skeleton variant="text" className="w-2/3 h-5" />
            <Skeleton variant="rectangular" height={40} />
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-3">
            <Skeleton variant="text" className="w-1/2 h-5" />
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={20} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
